import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { onMounted, onBeforeUnmount, ref, watch } from 'vue'
import '@micrio/client' // side effect: registers the <micr-io> custom element
import type { HTMLMicrioElement, Models } from '@micrio/client'
import { HOME_MICRIO_ID, PAGES, pageByTitle, type PageConfig } from '@/config/pages'
import {
	PANEL_IN_ARTWORK,
	PANEL_IN_ARTWORK_OPEN,
	PLATE_DIGIT_HEIGHT,
	PLATE_IN_ARTWORK,
	lockerKey,
	parseLockerTitle,
	parseSubmarkerTitle,
	type LockerRef,
} from '@/config/lockers'

import { waitForElement } from '@/helpers'

// v7 leaves three `instanceof TouchEvent` checks unguarded, one of them in the
// dblclick handler, and Firefox on macOS has no such global — so a double click
// throws ReferenceError. A stand-in nothing can ever be an instance of gives
// those checks the answer they want. It has to stay below the import: Micrio
// reads the real global once at module scope to decide whether touch exists.
const DOOR_BUILD_TIMEOUT = 3000

// The <micr-io id> attribute only drives the *initial* load; later changes go
// through open(), so this instance and its WebGL context are never torn down.
// eslint-disable-next-line vue/no-setup-props-destructure
type ImageRect = { x0: number; y0: number; x1: number; y1: number }

/** Anything pinned to a footprint and re-projected to the screen every frame. */
type Placed = {
	win: HTMLElement
	img: ImageRect
	/** Screen width at build time — later frames scale relative to this. */
	baseW: number
	/** Last transform written, so idle frames can skip the DOM write. */
	lastTransform?: string
}

type Door = Placed & {
	title: string
	page: PageConfig
}

type Locker = Placed & {
	title: string
	ref: LockerRef
	closedImg: ImageRect
	openImg: ImageRect
	isOpen: boolean
	lowerTimer?: number
}

type MicrioMarker = {
	id?: string
	i18n?: Record<string, { title?: string } | undefined>
	clickableArea?: { area?: unknown }
}

type MicrioBundleImage = {
	info?: { width?: number; height?: number }
	data?: { markers?: MicrioMarker[] }
}

const PLATE_FONT_SCALE = 1.387

/**
 * The number, as a child of the closed face so the swing's rotateY() carries it.
 * `faceH` is the face's build-time height in px: font-size has to be absolute for
 * the container's scale() to grow it, and percentages resolve against the parent
 * font-size rather than its box.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'MicrioViewer',
  props: {
    imageId: {},
    doorDuration: { default: 700 },
    pauseBetween: { default: 0 },
    zoomDuration: { default: 1000 },
    doorZoomOvershoot: { default: 1 },
    handoffDuration: { default: 250 },
    handoffHold: { default: 200 },
    lockerZoomDuration: { default: 800 },
    lockerZoomPadding: { default: 1.2 },
    lockerOpenDuration: { default: 600 }
  },
  emits: ["door-activate"],
  setup(__props: any, { emit: __emit }) {

if (!('TouchEvent' in window)) Object.assign(window, { TouchEvent: class TouchEvent extends UIEvent {} })

// All durations are in ms.
const props = __props

const emit = __emit

const initialImageId = props.imageId
const micrioSettings: Partial<Models.ImageInfo.Settings> = { clusterMarkerRadius: 48 }

const micrioEl = ref<HTMLMicrioElement>()
const doorLayer = ref<HTMLElement>()
const doorsEl = ref<HTMLElement>()
const previewEl = ref<HTMLElement>()
const isTransitioning = ref(false)

/** A footprint in image space (0..1), independent of Micrio's DOM. */
const doors = new Map<string, Door>()
const lockers = new Map<string, Locker>()

// Footprints cached across navigations: once measured we never need Micrio's
// embed DOM again, so a repeat visit places overlays the instant the image
// loads instead of ~250ms later when Micrio renders its embeds.
const doorGeometry = new Map<string, ImageRect>()
const lockerGeometry = new Map<string, { ref: LockerRef; panel: ImageRect }>()
let lockerGeometryFor: string | undefined
/** Holds the generated submarker gating rules; see writeSubmarkerRules. */
let submarkerStyle: HTMLStyleElement | undefined
let lockerImageSize: { w: number; h: number } | undefined

/** A single ref is what makes "only one open at a time" structural. */
const openLocker = ref<string | undefined>()

// The camera survives navigation, so an image would otherwise be re-opened
// still holding the zoom we left it at.
const savedViews = new Map<string, Models.Camera.View>()

/** The door currently mid-transition — its rect drives the preview's clip. */
let activeDoor: Door | undefined
/** Once released, the still covers the whole viewport for the swap. */
let clipReleased = false
let buildTimer: ReturnType<typeof setInterval> | undefined
let buildDeadline = 0
let rafId: number | undefined

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
const expectsDoors = (id: string) => id === HOME_MICRIO_ID
const expectsLockers = (id: string) => id === PAGES.bovenbouw.micrioId

const micrioPreviews = new Map<string, string>()

/**
 * URL of Micrio's own base tile, so the doorway shows the live artwork rather
 * than a hand-exported copy that goes stale.
 *
 * DeepZoom levels scale by 2^(level - maxLevel), so the largest level that still
 * fits inside one tile gives a single-request preview. Stitching is not an
 * option: the tile CDN refuses CORS. The publish revision doubles as a
 * cache-buster, so re-publishing in the dashboard is picked up automatically.
 */
async function resolveMicrioPreview(micrioId: string): Promise<string | undefined> {
	const cached = micrioPreviews.get(micrioId)
	if (cached) return cached
	try {
		const res = await fetch(`https://viewer.micr.io/${micrioId}/bundle.json`)
		if (!res.ok) return undefined
		const info = (await res.json())?.images?.[0]?.info
		const width = Number(info?.width)
		const height = Number(info?.height)
		if (!width || !height) return undefined

		const tileSize = Number(info.tileSize) || 1024
		const base = info.tileBasePath || info.path || 'https://eu.micr.io/'
		const maxLevel = Math.ceil(Math.log2(Math.max(width, height)))
		let level = maxLevel
		while (level > 0) {
			const scale = Math.pow(2, level - maxLevel)
			if (Math.ceil(width * scale) <= tileSize && Math.ceil(height * scale) <= tileSize) break
			level--
		}

		const ext = info.isWebP === false ? 'jpg' : 'webp'
		const revision = info.revision && typeof info.revision === 'object' ? Object.values(info.revision)[0] : undefined
		const url = `${base}${micrioId}/${level}/0_0.${ext}${revision ? `?v=${revision}` : ''}`
		micrioPreviews.set(micrioId, url)
		return url
	} catch {
		return undefined // preview is cosmetic — never let this break the transition
	}
}

function previewUrlFor(page: PageConfig): string | undefined {
	const src = page.previewImage
	if (src) {
		// Bundled imports, data URIs and absolute URLs already carry the public path;
		// only a bare filename is treated as living in public/.
		if (/^(https?:)?\/\/|^\/|^data:/.test(src)) return src
		return `${process.env.BASE_URL ?? '/'}${src}`
	}
	return micrioPreviews.get(page.micrioId)
}

/**
 * Warm every destination preview up front. Hover-based preloading was rejected:
 * this runs on tablets and smartboards, where hover doesn't exist.
 */
let previewsPreloaded = false
async function preloadPreviews() {
	if (previewsPreloaded) return
	previewsPreloaded = true
	await Promise.all(
		Object.values(PAGES).map(async (page) => {
			if (!page.previewImage) await resolveMicrioPreview(page.micrioId)
			const url = previewUrlFor(page)
			if (url) new Image().src = url
			// Ours carries a ?v= cache-buster, so Micrio's own request is a different
			// cache entry. Without warming it too, the destination downloads its base
			// tile at swap time and its first paint lands late.
			const own = micrioPreviews.get(page.micrioId)
			if (own?.includes('?')) new Image().src = own.split('?')[0]
		})
	)
}

/**
 * Project every overlay's footprint to the screen and write its transform.
 * Reads are pure math, so there are no layout reads to thrash.
 */
function syncOverlays(): boolean {
	const camera = micrioEl.value?.camera
	if (!camera || (!doors.size && !lockers.size)) return false

	const frames: Array<{ item: Placed; l: number; t: number; w: number; h: number }> = []
	const project = (item: Placed) => {
		const [x0, y0] = camera.getXY(item.img.x0, item.img.y0)
		const [x1, y1] = camera.getXY(item.img.x1, item.img.y1)
		frames.push({ item, l: x0, t: y0, w: x1 - x0, h: y1 - y0 })
	}
	doors.forEach(project)
	lockers.forEach(project)

	let changed = false
	frames.forEach(({ item, l, t, w }) => {
		const scale = item.baseW ? w / item.baseW : 1
		const transform = `translate(${l}px, ${t}px) scale(${scale})`
		if (transform === item.lastTransform) return
		item.win.style.transform = transform
		item.lastTransform = transform
		changed = true
	})

	// The doorway is a window onto the unscaled still: clip it to the door.
	if (activeDoor && !clipReleased && previewEl.value) {
		const f = frames.find((x) => x.item === activeDoor)
		if (f) {
			const right = Math.max(0, window.innerWidth - (f.l + f.w))
			const bottom = Math.max(0, window.innerHeight - (f.t + f.h))
			previewEl.value.style.clipPath = `inset(${Math.max(0, f.t)}px ${right}px ${bottom}px ${Math.max(0, f.l)}px)`
		}
	}
	return changed
}

/**
 * v7 dispatches `draw` once per rendered frame, at the end of its own frame
 * callback — after the camera for that frame is committed, before the browser
 * paints. Projecting here reads exactly the state the canvas is about to show,
 * so the overlays cannot trail the zoom by a frame the way a separately
 * scheduled rAF can. (v6 fired it roughly 5 times per 800ms fly, which is why
 * the loop below used to be the only driver.)
 */
function onDraw() {
	syncOverlays()
	startSync()
}

/**
 * Fallback for frames Micrio does not render — a resize, or an overlay built
 * while the camera is still. Where both run in one frame the second is free:
 * the projection is identical, so lastTransform skips the write.
 */
function startSync() {
	if (rafId !== undefined) return
	let idleFrames = 0
	const tick = () => {
		const changed = syncOverlays()
		idleFrames = changed || isTransitioning.value ? 0 : idleFrames + 1
		if (idleFrames > 20) {
			rafId = undefined
			return
		}
		rafId = requestAnimationFrame(tick)
	}
	rafId = requestAnimationFrame(tick)
}

function stopSync() {
	if (rafId !== undefined) cancelAnimationFrame(rafId)
	rafId = undefined
}

function buildDoor(title: string, page: PageConfig, img: ImageRect): boolean {
	const camera = micrioEl.value?.camera
	const layer = doorsEl.value
	if (!camera || !layer || doors.has(title)) return false

	const [x0, y0] = camera.getXY(img.x0, img.y0)
	const [x1, y1] = camera.getXY(img.x1, img.y1)
	const w = x1 - x0
	const h = y1 - y0
	if (!(w > 1 && h > 1)) return false // not projectable yet

	const win = document.createElement('div')
	win.className = 'door-window'
	win.dataset.handleSide = page.handleSide
	win.dataset.title = title
	win.style.width = `${w}px`
	win.style.height = `${h}px`
	win.style.setProperty('--door-duration', `${props.doorDuration}ms`)

	const leaf = document.createElement('div')
	leaf.className = 'door-leaf'
	leaf.style.backgroundImage = `url('${page.leafImage}')`
	win.appendChild(leaf)
	layer.appendChild(win)

	doors.set(title, { title, page, win, img, baseW: w })
	return true
}

/** The subset of Micrio's published marker JSON that we rely on. */
async function fetchBundleImage(micrioId: string): Promise<MicrioBundleImage | undefined> {
	try {
		const res = await fetch(`https://viewer.micr.io/${micrioId}/bundle.json`)
		if (!res.ok) return undefined
		return (await res.json())?.images?.[0]
	} catch {
		return undefined // callers all have a fallback or simply render nothing
	}
}

async function fetchMarkers(micrioId: string): Promise<MicrioMarker[]> {
	return (await fetchBundleImage(micrioId))?.data?.markers ?? []
}

/** Micrio stores titles per language; any language gives the same slug. */
function markerTitle(marker: MicrioMarker): string {
	const translations = Object.values(marker.i18n ?? {})
	return String(translations[0]?.title ?? '')
		.trim()
		.toLowerCase()
}

/**
 * `clickableArea.area` is `[x, y, width, height]` in normalised image
 * coordinates. Markers without one (the `_video` / `_info` siblings) yield
 * undefined.
 */
function markerRect(marker: MicrioMarker): ImageRect | undefined {
	const area = marker.clickableArea?.area
	if (!Array.isArray(area) || area.length < 4) return undefined
	const [x, y, w, h] = area.map(Number)
	if (!(w > 0 && h > 0)) return undefined
	return { x0: x, y0: y, x1: x + w, y1: y + h }
}

/**
 * The scale at which the doorway covers the viewport, so the zoom always travels
 * fully through the frame.
 *
 * Micrio's scale is device pixels per image pixel, so a fixed number lands at a
 * different on-screen size for every devicePixelRatio and viewport — which is why
 * the same value travelled through the frame on one browser and stopped at a
 * third of the width on another. Derived from the image's own pixel size, dpr
 * cancels out of the resulting travel entirely and every display gets the same
 * transition.
 *
 * Dimensions come from the live image rather than our own bundle cache, which
 * holds only the door rects and never the image's pixel size.
 */
function doorCoverScale(img: ImageRect): number | undefined {
	const info = micrioEl.value?.$current?.$info
	const box = micrioEl.value?.getBoundingClientRect()
	const iw = Number(info?.width)
	const ih = Number(info?.height)
	if (!(iw > 0 && ih > 0) || !box || box.width < 1 || box.height < 1) return undefined

	const doorW = (img.x1 - img.x0) * iw
	const doorH = (img.y1 - img.y0) * ih
	if (!(doorW > 0 && doorH > 0)) return undefined

	const dpr = window.devicePixelRatio || 1
	return dpr * Math.max(box.width / doorW, box.height / doorH) * Math.max(1, props.doorZoomOvershoot)
}

/** Micrio renders nothing for these markers, so the fetch is the only source. */
async function loadDoorGeometry() {
	if (doorGeometry.size) return
	const markers = await fetchMarkers(HOME_MICRIO_ID)
	markers.forEach((marker) => {
		const title = markerTitle(marker)
		if (!title || !pageByTitle(title)) return
		const rect = markerRect(marker)
		if (rect) doorGeometry.set(title, rect)
	})
}

/**
 * Grow a panel footprint into the full artwork footprint. A marker traces the
 * door's panel only, so drawing the artwork — which includes the handle
 * sticking out to the left — into the marker box would squash it by ~12%.
 */
function artworkRectFromPanel(panel: ImageRect, inset = PANEL_IN_ARTWORK): ImageRect {
	const w = (panel.x1 - panel.x0) / inset.w
	const h = (panel.y1 - panel.y0) / inset.h
	const x0 = panel.x0 - w * inset.x
	const y0 = panel.y0 - h * inset.y
	return { x0, y0, x1: x0 + w, y1: y0 + h }
}

async function loadLockerGeometry(micrioId: string) {
	if (lockerGeometryFor === micrioId) return
	lockerGeometryFor = micrioId
	const image = await fetchBundleImage(micrioId)
	const w = Number(image?.info?.width)
	const h = Number(image?.info?.height)
	if (w > 0 && h > 0) lockerImageSize = { w, h }
	const markers = image?.data?.markers ?? []
	const submarkers = new Map<string, string[]>()
	markers.forEach((marker) => {
		const title = markerTitle(marker)

		const sub = parseSubmarkerTitle(title)
		if (sub && marker.id) {
			const ids = submarkers.get(sub.parentKey) ?? []
			ids.push(marker.id)
			submarkers.set(sub.parentKey, ids)
			return
		}

		const ref = parseLockerTitle(title)
		if (!ref) return
		const panel = markerRect(marker)
		if (!panel) return
		lockerGeometry.set(lockerKey(ref.number), { ref, panel })
	})
	writeSubmarkerRules(submarkers)
}

function writeSubmarkerRules(submarkers: Map<string, string[]>) {
	const style = submarkerStyle ?? (submarkerStyle = document.createElement('style'))
	// The wrapper, not its inner button: hiding it takes the sibling <label> too.
	const marker = (id: string) => `micrio-marker[data-marker-id='${id.replace(/[\\']/g, '\\$&')}']`
	const all = [...submarkers.values()].flat()

	const rules = all.length ? [`${all.map((id) => `micr-io ${marker(id)}`).join(',')}{display:none}`] : []
	submarkers.forEach((ids, parentKey) => {
		rules.push(`micr-io[data-open-locker='${parentKey}'] :is(${ids.map(marker).join(',')}){display:revert}`)
	})

	style.textContent = rules.join('\n')
	if (!style.isConnected) document.head.appendChild(style)
}

/**
 * `is-open` poses the faces; `is-raised` is separate because stacking has to
 * outlast the pose — a door is still overlapping its neighbours for the whole
 * swing shut, so dropping both at once makes the closing door disappear behind
 * them.
 */
function applyLockerArt(locker: Locker, open: boolean): boolean {
	const wasOpen = locker.win.classList.contains('is-open')
	locker.isOpen = open
	locker.win.classList.toggle('is-open', open)

	if (wasOpen !== open) {
		window.clearTimeout(locker.lowerTimer)
		locker.lowerTimer = undefined
		if (open) {
			locker.win.classList.add('is-raised')
		} else {
			locker.lowerTimer = window.setTimeout(() => {
				locker.lowerTimer = undefined
				locker.win.classList.remove('is-raised')
			}, props.lockerOpenDuration)
		}
	}
	return true
}

function unionRect(a: ImageRect, b: ImageRect): ImageRect {
	return {
		x0: Math.min(a.x0, b.x0),
		y0: Math.min(a.y0, b.y0),
		x1: Math.max(a.x1, b.x1),
		y1: Math.max(a.y1, b.y1),
	}
}

/** Percentages, so the face rides the container's per-frame scale untouched. */
function placeFace(face: HTMLElement, rect: ImageRect, box: ImageRect) {
	const w = box.x1 - box.x0
	const h = box.y1 - box.y0
	face.style.left = `${((rect.x0 - box.x0) / w) * 100}%`
	face.style.top = `${((rect.y0 - box.y0) / h) * 100}%`
	face.style.width = `${((rect.x1 - rect.x0) / w) * 100}%`
	face.style.height = `${((rect.y1 - rect.y0) / h) * 100}%`
}

/**
 * Turns the target cap height into a font-size: Korolev Compressed Heavy's digits
 * measure 0.721em, so 1/0.721. A property of the face, so it has to be measured
 * rather than derived.
 */
function buildLockerNumber(ref: LockerRef, faceH: number): HTMLElement {
	const el = document.createElement('div')
	el.className = 'locker-number'
	el.textContent = ref.numberLabel
	el.style.left = `${PLATE_IN_ARTWORK.x * 100}%`
	el.style.top = `${PLATE_IN_ARTWORK.y * 100}%`
	el.style.width = `${PLATE_IN_ARTWORK.w * 100}%`
	el.style.height = `${PLATE_IN_ARTWORK.h * 100}%`
	el.style.fontSize = `${faceH * PLATE_IN_ARTWORK.h * PLATE_DIGIT_HEIGHT * PLATE_FONT_SCALE}px`
	return el
}

function buildLocker(title: string, ref: LockerRef, panel: ImageRect): boolean {
	const camera = micrioEl.value?.camera
	const layer = doorsEl.value
	if (!camera || !layer || lockers.has(title)) return false

	const closedImg = artworkRectFromPanel(panel, PANEL_IN_ARTWORK)
	const openImg = artworkRectFromPanel(panel, PANEL_IN_ARTWORK_OPEN)

	// Spanning both states keeps the container a fixed size across the swing and
	// gives the two faces one coordinate space, which is what lets them share a
	// hinge.
	const box = unionRect(closedImg, openImg)
	const [x0, y0] = camera.getXY(box.x0, box.y0)
	const [x1, y1] = camera.getXY(box.x1, box.y1)
	const w = x1 - x0
	const h = y1 - y0
	if (!(w > 1 && h > 1)) return false // not projectable yet

	const win = document.createElement('div')
	win.className = 'locker'
	win.dataset.title = title
	win.dataset.theme = ref.theme
	win.dataset.row = String(ref.row)
	win.dataset.col = String(ref.col)
	win.style.width = `${w}px`
	win.style.height = `${h}px`
	win.style.setProperty('--locker-phase', `${Math.max(0, props.lockerOpenDuration) / 2}ms`)
	// Set in the container's own coordinate space so the 3D strength is scaled by
	// the per-frame transform instead of flattening out as the camera zooms in.
	win.style.perspective = `${(x1 - x0) * 1.6}px`
	// The vanishing point must sit ON the hinge: at its default (the container's
	// centre) an edge-on face projects to a wedge rather than a line, showing as a
	// coloured sliver beside every closed locker.
	const hingePct = ((closedImg.x1 - box.x0) / (box.x1 - box.x0)) * 100
	win.style.perspectiveOrigin = `${hingePct}% 50%`

	// Both faces exist up front, so opening needs no image request mid-swing.
	const closedFace = document.createElement('div')
	closedFace.className = 'locker-face locker-face--closed'
	closedFace.style.backgroundImage = `url('${ref.image}')`
	placeFace(closedFace, closedImg, box)
	if (!ref.hasBakedNumber) {
		const faceH = (h * (closedImg.y1 - closedImg.y0)) / (box.y1 - box.y0)
		closedFace.appendChild(buildLockerNumber(ref, faceH))
	}

	const openFace = document.createElement('div')
	openFace.className = 'locker-face locker-face--open'
	openFace.style.backgroundImage = `url('${ref.imageOpen}')`
	placeFace(openFace, openImg, box)
	// The swung door is the only way to shut it. Only hit-testable while open —
	// see the pointer-events rule — so a closed locker's edge-on face is inert.
	openFace.addEventListener('click', (e) => {
		e.preventDefault()
		e.stopPropagation()
		if (openLocker.value === title) setOpenLocker(undefined)
	})

	// Open second, so it paints over the closed face as it unfolds.
	win.append(closedFace, openFace)
	layer.appendChild(win)

	const locker: Locker = {
		title,
		ref,
		win,
		img: box,
		baseW: w,
		closedImg,
		openImg,
		isOpen: false,
	}
	lockers.set(title, locker)
	if (openLocker.value === title) applyLockerArt(locker, true)
	return true
}

function buildLockers(): boolean {
	let built = 0
	lockerGeometry.forEach(({ ref, panel }, title) => {
		if (buildLocker(title, ref, panel)) built++
	})
	if (built) syncSubmarkerVisibility()
	return built > 0
}

/**
 * Centred on the clicked panel but sized to contain the opened locker too, since
 * the door swings out to the right and would otherwise be cut off the moment it
 * appears.
 *
 * Computed from the cached image-space rects, never the current projection:
 * getCoo() mid-flight yields garbage.
 */
function lockerFocus(locker: Locker): { cx: number; cy: number; w: number; h: number } {
	const p = locker.closedImg
	const o = locker.openImg
	const cx = (p.x0 + p.x1) / 2
	const cy = (p.y0 + p.y1) / 2

	const halfW = Math.max(cx - Math.min(p.x0, o.x0), Math.max(p.x1, o.x1) - cx)
	const halfH = Math.max(cy - Math.min(p.y0, o.y0), Math.max(p.y1, o.y1) - cy)
	const padded = 1 + Math.max(0, props.lockerZoomPadding)

	return { cx, cy, w: halfW * 2 * padded, h: halfH * 2 * padded }
}

/** Back out to the framing onLoad arrives at, so closing returns you there. */
function flyToFullView(camera: NonNullable<HTMLMicrioElement['camera']>, duration: number) {
	startSync()
	try {
		Promise.resolve(camera.flyToCoverView({ duration })).catch(() => {
			/* the camera still flies; only the promise misbehaves */
		})
	} catch (err) {
		console.warn('[micrio] locker zoom-out failed:', err)
	}
}

/**
 * The Micrio scale at which a region `w` x `h` (image fractions) exactly fits
 * the viewport. Micrio's scale is device pixels per image pixel, so fitting
 * means taking the smaller of the two axes.
 *
 * The fallback assumes the viewport and image share an aspect ratio; the exact
 * form does not.
 */
function fitScale(w: number, h: number): number | undefined {
	const camera = micrioEl.value?.camera
	if (!camera) return undefined
	const dpr = window.devicePixelRatio || 1
	const size = lockerImageSize
	if (size?.w && size.h) {
		return dpr * Math.min(window.innerWidth / (w * size.w), window.innerHeight / (h * size.h))
	}
	const cover = camera.getCoverScale?.()
	return cover ? cover / Math.max(w, h) : undefined
}

/** Zoom in, then swing open; closing swings shut, then zooms back out. */
async function setOpenLocker(title: string | undefined) {
	const next = openLocker.value === title ? undefined : title

	const locker = next ? lockers.get(next) : undefined
	const camera = micrioEl.value?.camera

	// Deliberately not gated behind isTransitioning, so clicking another locker
	// mid-close flies straight there instead of being swallowed.
	if (!locker) {
		commitOpenLocker(undefined)
		if (camera && props.lockerZoomDuration > 0) {
			await wait(props.lockerOpenDuration)
			flyToFullView(camera, props.lockerZoomDuration)
		}
		return
	}

	if (!camera || props.lockerZoomDuration <= 0) {
		commitOpenLocker(next)
		return
	}

	// Shut whatever is open before flying, so the old door is not left hanging
	// open while the camera moves somewhere else.
	commitOpenLocker(undefined)

	isTransitioning.value = true
	startSync()
	try {
		const focus = lockerFocus(locker)
		const scale = fitScale(focus.w, focus.h)
		if (scale) {
			// flyToCoo, not flyToView: flyToView's argument proved ambiguous between
			// corners and size. Micrio's fly promise REJECTS rather than resolving, so
			// it is not awaited and MUST be caught; the open is timed off our own
			// duration instead.
			Promise.resolve(camera.flyToCoo([focus.cx, focus.cy, scale], { duration: props.lockerZoomDuration })).catch(
				() => {
					/* the camera still flies; only the promise misbehaves */
				}
			)
		}
		await wait(props.lockerZoomDuration)
	} catch (err) {
		console.warn('[micrio] locker zoom failed, opening anyway:', err)
	} finally {
		isTransitioning.value = false
	}

	// A navigation during the flight rebuilds or clears the overlays, and wins.
	if (lockers.get(locker.title) === locker) commitOpenLocker(locker.title)
}

function commitOpenLocker(title: string | undefined) {
	openLocker.value = title
	lockers.forEach((locker) => applyLockerArt(locker, locker.title === title))
	syncSubmarkerVisibility()
	syncOverlays()
	startSync()
}

/**
 * Micrio owns submarker clicks and popups, so we only publish which locker is
 * open and let a CSS rule do the hiding. Being declarative, it survives Micrio
 * re-rendering its markers on zoom with no observer to maintain.
 */
function syncSubmarkerVisibility() {
	const root = micrioEl.value
	if (!root) return
	if (openLocker.value) root.dataset.openLocker = openLocker.value
	else delete root.dataset.openLocker
}

function buildFromCache(): boolean {
	let built = 0
	doorGeometry.forEach((img, title) => {
		const page = pageByTitle(title)
		if (page && buildDoor(title, page, img)) built++
	})
	return built > 0
}

/**
 * Removed outright rather than un-toggled: rebuilding doors closed is what stops
 * the leaf visibly swinging shut when a page arrives.
 */
function destroyOverlays() {
	doors.forEach((d) => d.win.remove())
	doors.clear()
	lockers.forEach((l) => {
		window.clearTimeout(l.lowerTimer)
		l.win.remove()
	})
	lockers.clear()
	activeDoor = undefined
	openLocker.value = undefined
	syncSubmarkerVisibility()
}

function revealLayer() {
	syncOverlays()
	startSync()
	clearTimeout(revealTimer)
	revealTimer = setTimeout(() => doorsEl.value?.classList.add('is-ready'), Math.max(0, props.handoffHold))
}

function fadeOutOverlays() {
	const layer = doorsEl.value
	const parent = layer?.parentElement
	if (!layer || !parent || !layer.classList.contains('is-ready') || !layer.childElementCount) return

	const ghost = layer.cloneNode(true) as HTMLElement
	parent.insertBefore(ghost, layer)
	// Next frame, or there is no start value to transition from.
	requestAnimationFrame(() => ghost.classList.remove('is-ready'))
	window.setTimeout(() => ghost.remove(), props.handoffDuration + 50)
}

let revealTimer: ReturnType<typeof setTimeout> | undefined
let holdTimer: ReturnType<typeof setTimeout> | undefined
function fadePreviewAfterHold() {
	const el = previewEl.value
	if (!el || !el.classList.contains('is-active')) return
	clearTimeout(holdTimer)
	holdTimer = setTimeout(fadePreviewOut, Math.max(0, props.handoffHold))
}

function fadePreviewOut() {
	const el = previewEl.value
	if (!el || !el.classList.contains('is-active')) return
	el.classList.add('is-fading')
	window.setTimeout(() => {
		el.classList.remove('is-active', 'is-fading')
		el.style.clipPath = ''
		el.style.backgroundImage = ''
	}, props.handoffDuration + 50)
}

/**
 * Nothing is revealed until the image has loaded *and* its overlays are placed,
 * so they never appear at different times.
 */
function prepareForImage(id: string) {
	clearInterval(buildTimer)
	buildTimer = undefined

	let build: (() => boolean) | undefined
	if (expectsDoors(id)) {
		build = buildFromCache
	} else if (expectsLockers(id)) {
		loadLockerGeometry(id) // async; the poller below picks it up when it lands
		build = buildLockers
	}

	if (!build) {
		revealLayer()
		fadePreviewAfterHold()
		return
	}

	const run = build
	buildDeadline = Date.now() + DOOR_BUILD_TIMEOUT
	const attempt = () => {
		const ok = run()
		if (ok || Date.now() > buildDeadline) {
			clearInterval(buildTimer)
			buildTimer = undefined
			revealLayer()
			fadePreviewAfterHold()
		}
	}
	attempt()
	if (!doors.size && !lockers.size) buildTimer = setInterval(attempt, 60)
}

function closeMicrioUi() {
	try {
		const root = micrioEl.value!
		root.$current?.state.marker.set(undefined)
		root.state.marker.set(undefined)
		root.state.popup.set(undefined)
		root.state.popover.set(undefined)
	} catch (err) {
		console.warn('[micrio] closing the open popup failed:', err)
	}
}

watch(
	() => props.imageId,
	(id, previous) => {
		if (!id || id === previous) return
		clearTimeout(revealTimer)
		closeMicrioUi()
		fadeOutOverlays() // takes a copy before the container is emptied
		destroyOverlays()
		doorsEl.value?.classList.remove('is-ready')
		const startView = savedViews.get(id)
		micrioEl.value?.open(id, startView ? { startView } : undefined)
		// The instance survives navigation, so without re-arming the guard only the
		// very first transition would ever run.
		isTransitioning.value = false
	}
)

/**
 * Click → swing the door open → (hold) → zoom to the handle → release the still
 * as a full-screen cover → let the parent navigate underneath it.
 */
async function onOverlayClick(e: MouseEvent) {
	const hit = (e.target as HTMLElement | null)?.closest<HTMLElement>('.door-leaf, .locker-face--closed')
	const title = hit?.closest<HTMLElement>('[data-title]')?.dataset.title?.trim().toLowerCase()
	if (!title || isTransitioning.value) return

	// Lockers share the handler, so they arrive here first. They open in place and
	// never navigate. Clicking one that is ALREADY open does nothing: closing is the
	// swung door's job, which leaves the cabinet interior free to be clicked for the
	// submarkers inside it.
	if (lockers.has(title)) {
		e.preventDefault()
		e.stopPropagation()
		if (openLocker.value !== title) setOpenLocker(title)
		return
	}

	const page = pageByTitle(title)
	if (!page) return

	e.preventDefault()
	e.stopPropagation()
	isTransitioning.value = true

	const door = doors.get(title)

	// Arm the still behind the leaf, clipped to this doorway.
	activeDoor = door
	clipReleased = false
	if (previewEl.value) {
		const url = previewUrlFor(page)
		previewEl.value.style.backgroundImage = url ? `url('${url}')` : ''
		previewEl.value.classList.remove('is-fading')
		previewEl.value.classList.add('is-active')
	}
	syncOverlays() // clip before the leaf moves, so nothing flashes
	startSync()

	if (door) {
		door.win.classList.add('is-open')
		await wait(props.doorDuration)
	}

	if (props.pauseBetween > 0) await wait(props.pauseBetween)

	const camera = micrioEl.value?.camera
	const targetScale = door ? doorCoverScale(door.img) : undefined
	try {
		if (camera && door && targetScale) {
			savedViews.set(props.imageId, Array.from(camera.getView()))
			const { x0, y0, x1, y1 } = door.img
			await camera.flyToCoo([(x0 + x1) / 2, (y0 + y1) / 2, targetScale], { duration: props.zoomDuration })
		}
	} catch (err) {
		console.warn('[micrio] zoom failed, navigating anyway:', err)
	}

	// Releasing the clip lets the still cover the viewport and hide the swap. The
	// doors then have to go, or the two unopened ones sit on top of it. The
	// preview is a sibling, so it survives until the new image reports loaded.
	clipReleased = true
	if (previewEl.value) previewEl.value.style.clipPath = 'none'
	destroyOverlays()
	doorsEl.value?.classList.remove('is-ready')

	emit('door-activate', { title })
}

/**
 * Micrio builds its generic web-embed iframe with allow="autoplay; fullscreen",
 * so the player inside is refused an EME key system and DRM titles never start.
 * (Its YouTube and marker-embed frames do ask for encrypted-media, hence the
 * check — those must not be touched, let alone reloaded.)
 *
 */
const pendingControlsRef = ref<AbortController | null>(null)
async function onMarkerOpened() {
	pendingControlsRef.value?.abort('superseded')
	const controller = new AbortController()
	pendingControlsRef.value = controller

	const deadline = setTimeout(() => controller.abort('timeout'), 3000)
	const frame = await waitForElement('micrio-popover iframe', micrioEl.value, controller.signal)
	clearTimeout(deadline)

	if (!frame) {
		console.warn('[micrio] popover iframe never appeared')
		return
	}
	frame.style.opacity = 0
	frame.allow = 'autoplay; fullscreen; encrypted-media; geolocation'

	const url = new URL(frame.src)
	url.searchParams.set('_t', Date.now())
	frame.src = url.toString()
	frame.style.opacity = 1
}

// micrio can't put a link on a marker picture so we lay our own link over it when the pop-up links to a PDF
function linkPdfPictures() {
	micrioEl.value?.querySelectorAll('micrio-swipe-gallery > micr-io').forEach((picture) => {
		if (picture.classList.contains('has-pdf-link')) return
		const pdf = picture.closest('dialog')?.querySelector<HTMLAnchorElement>('micrio-article a[href*=".pdf" i]')
		if (!pdf) return

		const link = document.createElement('a')
		link.className = 'pdf-image-link'
		link.href = pdf.href
		link.target = '_blank'
		link.rel = 'noopener noreferrer'
		link.setAttribute('aria-label', pdf.textContent?.trim() || 'Download het werkblad')

		picture.classList.add('has-pdf-link')
		picture.appendChild(link)
	})
}

// marker titles are internal slugs, so instead show the marker's embed title as the pop-up <h1>
function showEmbedTitles() {
	const root = micrioEl.value
	if (!root) return
	const markers = root.$current?.$data?.markers
	root.querySelectorAll<HTMLElement>('micrio-marker-content > h1').forEach((h1) => {
		if ('embedTitle' in h1.dataset) return
		h1.dataset.embedTitle = ''
		const title = h1.textContent?.trim()
		const embedTitle = markers
			?.flatMap((marker) => Object.values(marker.i18n ?? {}))
			.find((text) => text?.title?.trim() === title)
			?.embedTitle?.trim()
		if (embedTitle) h1.textContent = embedTitle
		else h1.remove()
	})
}

const micrioDom = new MutationObserver(() => {
	linkPdfPictures()
	showEmbedTitles()
})

const adoptedCameras = new WeakSet<object>()

/**
 * A cancelled flight rejects with no reason at all — the engine's abort path is
 * `t.T()`, called bare — and Micrio's own cluster-marker handler starts one
 * without catching. Interrupting that (double-clicking a cluster mid-zoom) leaves
 * a rejection nobody owns.
 *
 * Adopting it here changes nothing a caller can observe: the original promise is
 * still what gets returned, so our own awaits still reject and still hit their
 * try/catch. It only stops the rejection counting as unhandled. Suppressing the
 * report instead was tried and does not work — the dev-server overlay reports
 * unhandled rejections without checking preventDefault(), and registers its
 * listener before ours anyway.
 *
 * On the prototype, so one pass covers every image's camera; flyToFullView and
 * flyToCoverView delegate to flyToCoo and are covered with it.
 */
function adoptAbortedFlights(camera: NonNullable<HTMLMicrioElement['camera']>) {
	const proto = Object.getPrototypeOf(camera) as Record<string, unknown>
	if (adoptedCameras.has(proto)) return
	adoptedCameras.add(proto)

	for (const name of ['flyToView', 'flyToCoo', 'zoom']) {
		const fly = proto[name]
		if (typeof fly !== 'function') continue
		proto[name] = function (this: unknown, ...args: unknown[]) {
			const flight = (fly as (...a: unknown[]) => unknown).apply(this, args)
			if (flight instanceof Promise)
				flight.catch(() => {
					/* adopting only; a caller that wants the rejection still gets it */
				})
			return flight
		}
	}
}

function onLoad(e: Event) {
	const id = (e as CustomEvent).detail?.id as string | undefined
	const camera = micrioEl.value?.camera
	if (camera) adoptAbortedFlights(camera)
	// Match the still's cover framing so the cross-fade has nothing to reveal.
	if (id && id !== HOME_MICRIO_ID) {
		Promise.resolve(micrioEl.value?.camera?.flyToCoverView({ duration: 0 })).catch(() => {
			/* framing is cosmetic — never block the handoff on it */
		})
	}
	prepareForImage(id ?? props.imageId)
}

onMounted(() => {
	const root = micrioEl.value
	if (!root) return
	// One value on the shared parent: the still and the overlays cannot drift apart.
	doorLayer.value?.style.setProperty('--handoff-duration', `${props.handoffDuration}ms`)
	root.addEventListener('marker-opened', onMarkerOpened)
	root.addEventListener('load', onLoad)
	root.addEventListener('click', onOverlayClick, true)
	// These only report that movement *started*, so they just wake the rAF loop.
	root.addEventListener('move', startSync)
	root.addEventListener('zoom', startSync)
	root.addEventListener('draw', onDraw)
	window.addEventListener('resize', startSync)
	micrioDom.observe(root, { childList: true, subtree: true })

	// On a cold load Micrio can fire 'load' before this listener exists, so kick
	// the build off here too. prepareForImage polls and is idempotent.
	loadDoorGeometry()
	prepareForImage(props.imageId)
	preloadPreviews()
})

onBeforeUnmount(() => {
	const root = micrioEl.value
	root?.removeEventListener('marker-opened', onMarkerOpened)
	root?.removeEventListener('load', onLoad)
	root?.removeEventListener('click', onOverlayClick, true)
	root?.removeEventListener('move', startSync)
	root?.removeEventListener('zoom', startSync)
	root?.removeEventListener('draw', onDraw)
	window.removeEventListener('resize', startSync)
	micrioDom.disconnect()
	clearInterval(buildTimer)
	clearTimeout(revealTimer)
	clearTimeout(holdTimer)
	submarkerStyle?.remove()
	submarkerStyle = undefined
	stopSync()
	destroyOverlays()
})

return (_ctx: any,_cache: any) => {
  const _component_micr_io = _resolveComponent("micr-io")!

  return (_openBlock(), _createBlock(_component_micr_io, {
    ref_key: "micrioEl",
    ref: micrioEl,
    id: _unref(initialImageId),
    ".defaultSettings": micrioSettings,
    "data-light-mode": "",
    "data-logo": false
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        ref_key: "doorLayer",
        ref: doorLayer,
        class: "door-layer",
        "data-scroll-through": ""
      }, [
        _createElementVNode("div", {
          ref_key: "previewEl",
          ref: previewEl,
          class: "door-preview"
        }, null, 512),
        _createElementVNode("div", {
          ref_key: "doorsEl",
          ref: doorsEl,
          class: "door-doors"
        }, null, 512)
      ], 512)
    ]),
    _: 1
  }, 40, ["id"]))
}
}

})